import { useTranslation } from "react-i18next";
import React from "react";

/**
 * Sélecteur d'indicatif pays + saisie du numéro national.
 * Le couple (indicatif + numéro) forme l'identifiant E.164 du compte client.
 */
export const COUNTRIES = [
  { code: "224", flag: "🇬🇳", name: "Guinée", len: [8, 9] },
  { code: "33", flag: "🇫🇷", name: "France", len: [9] },
  { code: "1", flag: "🇺🇸", name: "USA / Canada", len: [10] },
  { code: "32", flag: "🇧🇪", name: "Belgique", len: [8, 9] },
  { code: "49", flag: "🇩🇪", name: "Allemagne", len: [10, 11] },
  { code: "34", flag: "🇪🇸", name: "Espagne", len: [9] },
  { code: "39", flag: "🇮🇹", name: "Italie", len: [9, 10] },
  { code: "351", flag: "🇵🇹", name: "Portugal", len: [9] },
  { code: "44", flag: "🇬🇧", name: "Royaume-Uni", len: [10] },
  { code: "31", flag: "🇳🇱", name: "Pays-Bas", len: [9] },
  { code: "41", flag: "🇨🇭", name: "Suisse", len: [9] },
  { code: "352", flag: "🇱🇺", name: "Luxembourg" },
  { code: "46", flag: "🇸🇪", name: "Suède" },
  { code: "47", flag: "🇳🇴", name: "Norvège" },
  { code: "45", flag: "🇩🇰", name: "Danemark" },
  { code: "353", flag: "🇮🇪", name: "Irlande" },
  { code: "43", flag: "🇦🇹", name: "Autriche" },
  { code: "30", flag: "🇬🇷", name: "Grèce" },
  { code: "7", flag: "🇷🇺", name: "Russie" },
  { code: "90", flag: "🇹🇷", name: "Turquie", len: [10] },
  { code: "221", flag: "🇸🇳", name: "Sénégal", len: [9] },
  { code: "223", flag: "🇲🇱", name: "Mali", len: [8] },
  { code: "225", flag: "🇨🇮", name: "Côte d'Ivoire", len: [10] },
  { code: "231", flag: "🇱🇷", name: "Liberia" },
  { code: "232", flag: "🇸🇱", name: "Sierra Leone", len: [8] },
  { code: "233", flag: "🇬🇭", name: "Ghana", len: [9] },
  { code: "234", flag: "🇳🇬", name: "Nigeria", len: [10] },
  { code: "245", flag: "🇬🇼", name: "Guinée-Bissau" },
  { code: "220", flag: "🇬🇲", name: "Gambie" },
  { code: "222", flag: "🇲🇷", name: "Mauritanie" },
  { code: "226", flag: "🇧🇫", name: "Burkina Faso" },
  { code: "227", flag: "🇳🇪", name: "Niger" },
  { code: "228", flag: "🇹🇬", name: "Togo" },
  { code: "229", flag: "🇧🇯", name: "Bénin" },
  { code: "237", flag: "🇨🇲", name: "Cameroun", len: [9] },
  { code: "241", flag: "🇬🇦", name: "Gabon" },
  { code: "242", flag: "🇨🇬", name: "Congo" },
  { code: "243", flag: "🇨🇩", name: "RD Congo" },
  { code: "212", flag: "🇲🇦", name: "Maroc", len: [9] },
  { code: "213", flag: "🇩🇿", name: "Algérie", len: [9] },
  { code: "216", flag: "🇹🇳", name: "Tunisie", len: [8] },
  { code: "20", flag: "🇪🇬", name: "Égypte" },
  { code: "27", flag: "🇿🇦", name: "Afrique du Sud", len: [9] },
  { code: "254", flag: "🇰🇪", name: "Kenya" },
  { code: "250", flag: "🇷🇼", name: "Rwanda" },
  { code: "966", flag: "🇸🇦", name: "Arabie saoudite", len: [9] },
  { code: "971", flag: "🇦🇪", name: "Émirats arabes unis", len: [9] },
  { code: "974", flag: "🇶🇦", name: "Qatar" },
  { code: "965", flag: "🇰🇼", name: "Koweït" },
  { code: "86", flag: "🇨🇳", name: "Chine", len: [11] },
  { code: "91", flag: "🇮🇳", name: "Inde", len: [10] },
  { code: "55", flag: "🇧🇷", name: "Brésil" },
  { code: "61", flag: "🇦🇺", name: "Australie", len: [9] },
];

/** Chiffres nationaux nettoyés (zéros de tête retirés). */
export function cleanNational(national) {
  return String(national || "").replace(/\D/g, "").replace(/^0+/, "");
}

/** Construit l'identifiant E.164, ex : buildE164("33", "0612345678") → "+33612345678" */
export function buildE164(code, national) {
  const d = cleanNational(national);
  return d ? `+${code}${d}` : "";
}

/** Valide le numéro national selon les règles du pays choisi (6-12 chiffres par défaut). */
export function isValidNational(code, national) {
  const d = cleanNational(national);
  if (!d) return false;
  const c = COUNTRIES.find((x) => x.code === code);
  if (c?.len) return c.len.includes(d.length);
  return d.length >= 6 && d.length <= 12;
}

export default function PhoneInput({ code, onCodeChange, value, onChange, testId = "phone-input", placeholder, required = false, autoComplete = "tel" }) {
  const { t } = useTranslation();
  const selected = COUNTRIES.find((c) => c.code === code) || COUNTRIES[0];
  return (
    <div className="flex items-stretch gap-2">
      <div className="relative shrink-0">
        <select
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          data-testid={`${testId}-country`}
          aria-label={t("phi.countryCode")}
          className="h-full appearance-none bg-elevated rounded-xl pl-3 pr-7 py-3 text-sm outline-none cursor-pointer"
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.flag} +{c.code} {c.name}</option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-mute">▼</span>
      </div>
      <input
        data-testid={testId}
        type="tel"
        inputMode="tel"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || (selected.code === "224" ? "6XX XX XX XX" : t("phi.nationalNumber"))}
        className="flex-1 min-w-0 bg-elevated rounded-xl px-3 py-3 outline-none"
        autoComplete={autoComplete}
      />
    </div>
  );
}
